import { useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { Currency } from 'components/Number';
import PropTypes from 'prop-types';

import { forecastStartMonth, formatMonthLabel } from './cashflowFeeExposureHelpers';
import { buildSourcesAndUsesRows } from './cashflowFeeExposureExport';

const SECTION_BG = '#1F4E79';
const SECTION_COLOR = '#FFFFFF';

function SourcesAndUsesTable({ customers, pipelineClients, months, tenantFilter }) {
  const forecastMonth = forecastStartMonth();
  const model = useMemo(
    () => buildSourcesAndUsesRows({
      customers,
      pipelineClients,
      months: (months || []).map((month) => month.month || month),
      tenantFilter,
    }),
    [customers, pipelineClients, months, tenantFilter]
  );

  if (!model.months.length) return null;

  return (
    <Box overflowX="auto">
      <Box as="table" w="max-content" minW="100%" fontFamily="body" fontSize="sm">
        <thead>
          <tr>
            <th style={stickyHeader} />
            {model.months.map((month) => (
              <th
                key={`${month}-status`}
                style={{
                  ...monthHeader,
                  background: month < forecastMonth ? '#70AD47' : '#2E75B6',
                  borderLeft: month === forecastMonth ? '2px solid #422AFB' : undefined,
                }}
              >
                {month < forecastMonth ? 'Act.' : 'Proj.'}
              </th>
            ))}
          </tr>
          <tr>
            <th style={stickyHeader}>Line Item</th>
            {model.months.map((month) => (
              <th
                key={month}
                style={{
                  ...monthHeader,
                  background: month < forecastMonth ? '#70AD47' : '#2E75B6',
                  borderLeft: month === forecastMonth ? '2px solid #422AFB' : undefined,
                }}
              >
                {formatMonthLabel(month)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.rows.map((row, index) => {
            if (row.kind === 'spacer') {
              return <tr key={`spacer-${index}`}><td style={{ height: 12 }} /></tr>;
            }
            const section = row.kind === 'section';
            return (
              <tr key={`${row.label}-${index}`} style={{ background: section ? SECTION_BG : undefined }}>
                <td
                  style={{
                    ...stickyCell,
                    background: section ? SECTION_BG : '#fff',
                    color: section ? SECTION_COLOR : undefined,
                    fontWeight: section || row.kind === 'label' ? 700 : 500,
                  }}
                >
                  {row.label}
                </td>
                {model.months.map((month, monthIndex) => (
                  <td
                    key={`${row.label}-${month}`}
                    style={{
                      padding: '8px 12px',
                      textAlign: 'right',
                      color: section ? SECTION_COLOR : undefined,
                      background: section ? SECTION_BG : undefined,
                      borderLeft: month === forecastMonth ? '2px solid #422AFB' : undefined,
                    }}
                  >
                    {row.kind === 'amount' && row.values[monthIndex] != null ? (
                      <Currency value={row.values[monthIndex]} maximumFractionDigits={0} />
                    ) : ''}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}

const stickyHeader = {
  position: 'sticky',
  left: 0,
  background: '#fff',
  textAlign: 'left',
  padding: '8px 12px',
  zIndex: 1,
};

const monthHeader = {
  padding: '8px 12px',
  textAlign: 'right',
  color: '#fff',
  fontWeight: 700,
};

const stickyCell = {
  position: 'sticky',
  left: 0,
  padding: '8px 12px',
  zIndex: 1,
  minWidth: 280,
};

SourcesAndUsesTable.propTypes = {
  customers: PropTypes.array,
  pipelineClients: PropTypes.array,
  months: PropTypes.array,
  tenantFilter: PropTypes.string,
};

export default SourcesAndUsesTable;
